import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Grid, Card, CardContent, Typography, Box, TextField, Button, 
  List, ListItem, ListItemText, Chip, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, LinearProgress, Alert, Divider, Avatar, useTheme,
  InputAdornment, Pagination,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  FormControlLabel, Checkbox,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, 
  People as PeopleIcon, Business as BusinessIcon, Phone as PhoneIcon,
  Email as EmailIcon, LocationOn as LocationOnIcon, Search as SearchIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../api';
import { getUiPreferences, patchSavedFiltersKey } from '../userPreferencesApi';
import SendMessageDialog from '../components/SendMessageDialog';

const api = `${API_BASE_URL}clients`;

export default function Clients() {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const userRole = (() => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return null;
      const u = JSON.parse(raw);
      return u?.role || null;
    } catch {
      return null;
    }
  })();
  const mayDeleteClients = userRole && ['super_admin', 'admin'].includes(userRole);
  const mayManageSubscriptions = userRole && ['super_admin', 'admin', 'office_admin'].includes(userRole);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true); // first page load only
  const [fetching, setFetching] = useState(false); // subsequent requests (search/pagination)
  const [open, setOpen] = useState(false);
  const [sendMessageOpen, setSendMessageOpen] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [form, setForm] = useState({ 
    name: '', phone: '', email: '', address: '', notes: '', company: '', 
    bulstat: '', vat_number: '', city: '', postal_code: '', country: '', contact_person: '' 
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const searchDebounceRef = useRef(null);
  const skipClientSearchDebounceOnce = useRef(true);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [mineOnly, setMineOnly] = useState(false);
  const [portalStatus, setPortalStatus] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalUrlCopied, setPortalUrlCopied] = useState(false);
  const [portalAccessLogs, setPortalAccessLogs] = useState([]);
  const [portalLogsLoading, setPortalLogsLoading] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [servicePlans, setServicePlans] = useState([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [subscriptionBusy, setSubscriptionBusy] = useState(false);
  const [subForm, setSubForm] = useState({
    service_plan_id: '',
    next_service_date: '',
    preferred_time: '09:00',
    service_address: '',
    price_override: '',
  });

  // Initial load + restore list filters
  useEffect(() => {
    const c = getUiPreferences().savedFilters?.clients;
    const q = typeof c?.clientSearch === 'string' ? c.clientSearch : '';
    const p = Number(c?.page);
    const nextPage = Number.isFinite(p) && p > 0 ? p : 1;
    setClientSearch(q);
    setPage(nextPage);
    fetchClients(nextPage, q);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial sync only
  }, []);

  useEffect(() => {
    if (loading) return undefined;
    const t = setTimeout(() => {
      patchSavedFiltersKey('clients', { clientSearch, page }).catch(() => {});
    }, 800);
    return () => clearTimeout(t);
  }, [clientSearch, page, loading]);

  // Handle auto-opening modals from Dashboard state
  useEffect(() => {
    if (location.state?.action === 'create') {
      // Small delay to ensure state sets up properly before opening modal
      setTimeout(() => {
        handleOpen(null);
        // Clear state 
        navigate(location.pathname, { replace: true, state: {} });
      }, 100);
    }
  }, [location, navigate]);

  // Open client editor when linked from estimates (e.g. /clients?id=42)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('id');
    if (raw == null || raw === '') return undefined;
    const id = Number(raw);
    if (!Number.isFinite(id) || id < 1) return undefined;

    let cancelled = false;
    const token = localStorage.getItem('token');
    fetch(`${api}/${id}`, {
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Not found');
        return res.json();
      })
      .then((client) => {
        if (cancelled) return;
        handleOpen(client);
        navigate({ pathname: location.pathname, search: '' }, { replace: true });
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
    // handleOpen is stable for this screen; omit to avoid re-running on each render
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deep link once per ?id= change
  }, [location.search, location.pathname, navigate]);

  async function handleExportClientsCsv() {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_BASE_URL}export/clients`, {
        headers: { Accept: 'text/csv', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `clients-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Export failed');
    }
  }

  function fetchClients(nextPage = page, nextQuery = clientSearch) {
    const isInitial = clients.length === 0 && loading;
    if (!isInitial) setFetching(true);
    const token = localStorage.getItem('token');
    const qs = new URLSearchParams();
    qs.set('per_page', '10');
    qs.set('page', String(nextPage));
    const q = (nextQuery || '').trim();
    if (q) qs.set('q', q);
    if (mineOnly) qs.set('mine', '1');

    fetch(`${api}?${qs.toString()}`, { headers: token ? { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } : { 'Accept': 'application/json' } })
      .then(res => {
        const contentType = res.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('Server did not return JSON');
        }
        return res.json();
      })
      .then(data => {
        const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
        setClients(list);
        const meta = data?.meta || null;
        if (meta) {
          setPage(Number(meta.current_page) || nextPage);
          setLastPage(Number(meta.last_page) || 1);
          setTotal(Number(meta.total) || 0);
        } else {
          setPage(nextPage);
          setLastPage(1);
          setTotal(list.length);
        }
        setError('');
      })
      .catch(err => setError(err.message || 'Failed to load clients'))
      .finally(() => {
        setLoading(false);
        setFetching(false);
      });
  }

  // Debounce server-side search so typing doesn't blur the field.
  useEffect(() => {
    if (skipClientSearchDebounceOnce.current) {
      skipClientSearchDebounceOnce.current = false;
      return undefined;
    }
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      fetchClients(1, clientSearch);
    }, 250);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSearch]);

  async function fetchServicePlans() {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_BASE_URL}service-plans`, {
        headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const json = await res.json().catch(() => []);
      if (res.ok) setServicePlans(Array.isArray(json) ? json : []);
    } catch {
      setServicePlans([]);
    }
  }

  async function fetchSubscriptions(clientId) {
    if (!clientId || !mayManageSubscriptions) {
      setSubscriptions([]);
      return;
    }
    setSubscriptionsLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${api}/${clientId}/subscriptions`, {
        headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const json = await res.json().catch(() => []);
      if (res.ok) setSubscriptions(Array.isArray(json) ? json : []);
      else setSubscriptions([]);
    } catch {
      setSubscriptions([]);
    } finally {
      setSubscriptionsLoading(false);
    }
  }

  async function handleAddSubscription(e) {
    e.preventDefault();
    if (!editClient?.id || !subForm.service_plan_id || !subForm.next_service_date) return;
    setSubscriptionBusy(true);
    setError('');
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${api}/${editClient.id}/subscriptions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          service_plan_id: Number(subForm.service_plan_id),
          next_service_date: subForm.next_service_date,
          preferred_time: subForm.preferred_time || null,
          service_address: subForm.service_address || null,
          price_override: subForm.price_override === '' ? null : Number(subForm.price_override),
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Failed to add subscription');
      setSuccess('Service subscription added.');
      setSubForm((f) => ({ ...f, service_plan_id: '', next_service_date: '' }));
      await fetchSubscriptions(editClient.id);
    } catch (err) {
      setError(err.message || 'Failed to add subscription');
    } finally {
      setSubscriptionBusy(false);
    }
  }

  async function createSubscriptionInvoice(subId) {
    if (!editClient?.id) return;
    setSubscriptionBusy(true);
    setError('');
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${api}/${editClient.id}/subscriptions/${subId}/invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ send_email: true }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Failed to create invoice');
      setSuccess(json.skipped ? json.message : `Invoice ${json.invoice?.invoice_number || ''} created.`);
      await fetchSubscriptions(editClient.id);
    } catch (err) {
      setError(err.message || 'Failed to create invoice');
    } finally {
      setSubscriptionBusy(false);
    }
  }

  async function toggleSubscriptionAutoInvoice(sub, checked) {
    if (!editClient?.id) return;
    setSubscriptionBusy(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${api}/${editClient.id}/subscriptions/${sub.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ auto_invoice: checked }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Update failed');
      await fetchSubscriptions(editClient.id);
    } catch (err) {
      setError(err.message || 'Update failed');
    } finally {
      setSubscriptionBusy(false);
    }
  }

  async function subscriptionAction(subId, action) {
    if (!editClient?.id) return;
    setSubscriptionBusy(true);
    setError('');
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${api}/${editClient.id}/subscriptions/${subId}/${action}`, {
        method: 'POST',
        headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || `Failed to ${action}`);
      setSuccess(`Subscription ${action === 'cancel' ? 'cancelled' : action + 'd'}.`);
      await fetchSubscriptions(editClient.id);
    } catch (err) {
      setError(err.message || `Failed to ${action}`);
    } finally {
      setSubscriptionBusy(false);
    }
  }

  async function fetchPortalAccessLogs(clientId) {
    if (!clientId) {
      setPortalAccessLogs([]);
      return;
    }
    setPortalLogsLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${api}/${clientId}/portal-access-logs?limit=50`, {
        headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok) setPortalAccessLogs(Array.isArray(json.data) ? json.data : []);
      else setPortalAccessLogs([]);
    } catch {
      setPortalAccessLogs([]);
    } finally {
      setPortalLogsLoading(false);
    }
  }

  async function fetchPortalStatus(clientId) {
    if (!clientId) {
      setPortalStatus(null);
      return;
    }
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${api}/${clientId}/portal-link`, {
        headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok) setPortalStatus(json.data || null);
      else setPortalStatus(null);
    } catch {
      setPortalStatus(null);
    }
  }

  async function handleCreatePortalLink() {
    if (!editClient?.id) return;
    setPortalBusy(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${api}/${editClient.id}/portal-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ ttl_days: 90 }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Failed to create portal link');
      setPortalStatus(json.data || null);
      if (json.portal_url) {
        await navigator.clipboard.writeText(json.portal_url);
        setPortalUrlCopied(true);
        setTimeout(() => setPortalUrlCopied(false), 2500);
      }
      setSuccess('Client portal link created and copied to clipboard.');
      void fetchPortalAccessLogs(editClient.id);
    } catch (e) {
      setError(e.message || 'Failed to create portal link');
    } finally {
      setPortalBusy(false);
    }
  }

  async function handleRevokePortalLink() {
    if (!editClient?.id) return;
    if (!window.confirm('Revoke this client portal link? The client will no longer be able to open it.')) return;
    setPortalBusy(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${api}/${editClient.id}/portal-link`, {
        method: 'DELETE',
        headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Failed to revoke portal link');
      setPortalStatus(json.data || null);
      setSuccess('Client portal link revoked.');
      void fetchPortalAccessLogs(editClient.id);
    } catch (e) {
      setError(e.message || 'Failed to revoke portal link');
    } finally {
      setPortalBusy(false);
    }
  }

  function handleOpen(client = null) {
    setEditClient(client);
    setForm(client || { 
      name: '', phone: '', email: '', address: '', notes: '', company: '', 
      bulstat: '', vat_number: '', city: '', postal_code: '', country: '', contact_person: '' 
    });
    setPortalStatus(null);
    setPortalAccessLogs([]);
    setPortalUrlCopied(false);
    setSubscriptions([]);
    if (client?.id) {
      void fetchPortalStatus(client.id);
      void fetchPortalAccessLogs(client.id);
      if (mayManageSubscriptions) {
        void fetchServicePlans();
        void fetchSubscriptions(client.id);
        setSubForm((f) => ({
          ...f,
          service_address: client.address || '',
          next_service_date: new Date().toISOString().slice(0, 10),
        }));
      }
    }
    setOpen(true);
  }
  
  function handleClose() { 
    setOpen(false); 
    setEditClient(null);
    setPortalStatus(null);
    setPortalAccessLogs([]);
    setSubscriptions([]);
  }

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    const method = editClient ? 'PUT' : 'POST';
    const url = editClient ? `${api}/${editClient.id}` : api;
    
    const token = localStorage.getItem('token');
    fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      body: JSON.stringify(form)
    })
      .then(res => {
        const contentType = res.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('Server did not return JSON');
        }
        if (!res.ok) throw new Error('Failed to save client');
        return res.json();
      })
      .then(() => {
        setSuccess(editClient ? 'Client updated successfully!' : 'Client created successfully!');
        fetchClients(1, clientSearch);
        handleClose();
      })
      .catch(err => setError(err.message || 'Error saving client'));
  }

  function handleDelete(id) {
    if (!window.confirm('Delete this client?')) return;
    
    setError('');
    setSuccess('');
    
    const token = localStorage.getItem('token');
    fetch(`${api}/${id}`, { method: 'DELETE', headers: token ? { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } : { 'Accept': 'application/json' } })
      .then(async (res) => {
        const contentType = res.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('Server did not return JSON');
        }
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.message || data.error || 'Failed to delete');
        }
        return data;
      })
      .then(() => {
        setSuccess('Client deleted successfully!');
        fetchClients(page, clientSearch);
      })
      .catch(err => setError(err.message || 'Error deleting client'));
  }

  // With server-side pagination/search, the API already returns a page-sized list.
  const displayedClients = useMemo(() => clients, [clients]);

  if (loading && clients.length === 0) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress sx={{ width: '100%' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', pb: { xs: 1, sm: 0 } }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h3" 
          sx={{ 
            fontWeight: 700, 
            color: theme.palette.text.primary, 
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            lineHeight: 1.2,
          }}
        >
          Client Management
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            color: theme.palette.text.secondary,
            display: 'flex',
            alignItems: 'center',
            lineHeight: 1.4,
          }}
        >
          Manage your client database and contact information.
        </Typography>
      </Box>

      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Create Client Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: 'fit-content' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box sx={{ 
                  width: 48, height: 48, borderRadius: '12px', 
                  background: 'linear-gradient(135deg, #06b6d420 0%, #06b6d410 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#06b6d4'
                }}>
                  <AddIcon />
                </Box>
                <Box>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 700, 
                      color: theme.palette.text.primary,
                      fontSize: '1.5rem',
                      lineHeight: 1.2,
                    }}
                  >
                    Add New Client
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: theme.palette.text.secondary,
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      lineHeight: 1.4,
                    }}
                  >
                    Register a new client
                  </Typography>
                </Box>
              </Box>
              
              <Button
                variant="contained"
                fullWidth
                startIcon={<AddIcon />}
                onClick={() => handleOpen()}
                sx={{
                  background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
                  },
                  display: 'flex',
                  alignItems: 'center',
                  height: '40px',
                }}
              >
                Add Client
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Statistics Card */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box sx={{ 
                  width: 48, height: 48, borderRadius: '12px', 
                  background: 'linear-gradient(135deg, #10b98120 0%, #10b98110 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#10b981'
                }}>
                  <PeopleIcon />
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary, fontSize: '1.5rem' }}>
                    Client Overview
                  </Typography>
                  <Typography variant="h6" sx={{ color: theme.palette.text.secondary, fontWeight: 500, fontSize: '0.875rem' }}>
                    Total clients and statistics
                  </Typography>
                </Box>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, backgroundColor: 'rgba(6, 182, 212, 0.04)' }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#06b6d4', fontSize: '2rem' }}>
                      {total || clients.length}
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#64748b', fontWeight: 500, fontSize: '0.875rem' }}>
                      Total Clients
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, backgroundColor: 'rgba(16, 185, 129, 0.04)' }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#10b981', fontSize: '2rem' }}>
                      {clients.filter(c => c.company).length}
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#64748b', fontWeight: 500, fontSize: '0.875rem' }}>
                      Business Clients (this page)
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Clients List */}
        <Grid item xs={12}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  flexWrap: 'wrap',
                  alignItems: { xs: 'stretch', md: 'center' },
                  gap: 2,
                  mb: 3,
                  justifyContent: { xs: 'flex-start', md: 'space-between' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      background:
                        'linear-gradient(135deg, #8b5cf620 0%, #8b5cf610 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#8b5cf6',
                    }}
                  >
                    <PeopleIcon />
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1.5rem' }}
                  >
                    All Clients
                  </Typography>
                </Box>
                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  flexWrap: 'wrap',
                  gap: 1,
                  alignItems: { xs: 'stretch', sm: 'center' },
                  width: { xs: '100%', md: 'auto' },
                }}>
                  <Button
                    variant={mineOnly ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => {
                      const next = !mineOnly;
                      setMineOnly(next);
                      fetchClients(1, clientSearch);
                    }}
                    sx={{ textTransform: 'none', width: { xs: '100%', sm: 'auto' } }}
                  >
                    {mineOnly ? 'My clients' : 'All clients'}
                  </Button>
                  <TextField
                    size="small"
                    label="Search"
                    placeholder="Phone or name…"
                    value={clientSearch}
                    onChange={(e) => {
                      const v = e.target.value;
                      setClientSearch(v);
                    }}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 0, width: { xs: '100%', sm: 240 }, mt: { xs: 0.5, sm: 0 } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button variant="outlined" size="small" startIcon={<FileDownloadIcon />} onClick={handleExportClientsCsv} sx={{ textTransform: 'none', width: { xs: '100%', sm: 'auto' } }}>
                    Export CSV
                  </Button>
                </Box>
              </Box>

              {fetching && (
                <Box sx={{ mb: 2 }}>
                  <LinearProgress sx={{ width: '100%' }} />
                </Box>
              )}

              {(clientSearch.trim() || lastPage > 1) && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Showing {displayedClients.length} of {total || displayedClients.length} clients
                  </Typography>
                  {lastPage > 1 && (
                    <Pagination
                      count={lastPage}
                      page={page}
                      onChange={(_, p) => fetchClients(p, clientSearch)}
                      color="primary"
                      size="small"
                    />
                  )}
                </Box>
              )}

              {clients.length > 0 ? (
                displayedClients.length > 0 ? (
                <Grid container spacing={2}>
                  {displayedClients.map((client) => (
                    <Grid item xs={12} sm={6} md={4} key={client.id}>
                      <Card sx={{ 
                        height: '100%',
                        cursor: 'pointer',
                        '&:hover': { boxShadow: 3 },
                        transition: 'box-shadow 0.3s ease-in-out',
                      }}
                      onClick={() => handleOpen(client)}
                      >
                        <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Box sx={{ 
                              width: 48, height: 48, borderRadius: '12px', 
                              background: 'linear-gradient(135deg, #8b5cf620 0%, #8b5cf610 100%)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: '#8b5cf6'
                            }}>
                              <PeopleIcon />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5, fontSize: '1rem' }}>
                                {client.name}
                              </Typography>
                              <Typography variant="body1" sx={{ color: '#64748b', fontSize: '0.875rem' }}>
                                {client.email}
                              </Typography>
                              {client.created_by && (
                                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.25 }}>
                                  Added by {client.created_by.name || client.created_by.email}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" sx={{ color: '#64748b', mb: 1, fontSize: '0.875rem' }}>
                              {client.phone || 'No phone'}
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#64748b', mb: 2, fontSize: '0.875rem' }}>
                              {client.address || 'No address'}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Chip 
                              label={client.is_company ? 'Company' : 'Private'} 
                              size="small"
                              sx={{
                                backgroundColor: client.is_company ? '#3b82f6' : '#8b5cf6',
                                color: 'white',
                                fontWeight: 600,
                              }}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                aria-label="Edit client"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpen(client);
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                              {mayDeleteClients && (
                                <Tooltip title="Delete client">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    aria-label="Delete client"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDelete(client.id);
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography
                      variant="h6"
                      sx={{ color: '#64748b', fontWeight: 500, fontSize: '0.875rem' }}
                    >
                      {`No clients match "${clientSearch.trim()}" (phone or name)`}
                    </Typography>
                  </Box>
                )
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" sx={{ color: '#64748b', fontWeight: 500, fontSize: '0.875rem' }}>
                    No clients found. Add your first client to get started.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Create/Edit Client Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
          {editClient ? 'Edit Client' : 'Add New Client'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            {/* Basic Information Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 2, color: theme.palette.text.primary, fontWeight: 700, fontSize: '1.5rem' }}>
                Basic Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="name"
                    label="Client Name *"
                    value={form.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter client name"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="company"
                    label="Company Name"
                    value={form.company}
                    onChange={handleChange}
                    placeholder="Enter company name"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="email"
                    label="Email *"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    placeholder="client@example.com"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="phone"
                    label="Phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="+44 20 7946 0958"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="contact_person"
                    label="Contact Person"
                    value={form.contact_person}
                    onChange={handleChange}
                    placeholder="Primary contact person"
                  />
                </Grid>
              </Grid>
            </Box>
            
            {/* UK Business Information Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 2, color: theme.palette.text.primary, fontWeight: 700, fontSize: '1.5rem' }}>
                UK Business Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="vat_number"
                    label="VAT Number"
                    value={form.vat_number}
                    onChange={handleChange}
                    placeholder="GB123456789"
                    helperText="Format: GB + 9 digits"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="bulstat"
                    label="Company Registration Number"
                    value={form.bulstat}
                    onChange={handleChange}
                    placeholder="12345678"
                    helperText="8-digit company registration number"
                  />
                </Grid>
              </Grid>
            </Box>
            
            {/* Address Information Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 2, color: theme.palette.text.primary, fontWeight: 700, fontSize: '1.5rem' }}>
                Address Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="address"
                    label="Street Address"
                    value={form.address}
                    onChange={handleChange}
                    multiline
                    rows={2}
                    placeholder="Enter street address"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    name="city"
                    label="City"
                    value={form.city}
                    onChange={handleChange}
                    placeholder="London"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    name="postal_code"
                    label="Postal Code"
                    value={form.postal_code}
                    onChange={handleChange}
                    placeholder="SW1A 1AA"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    name="country"
                    label="Country"
                    value={form.country}
                    onChange={handleChange}
                    defaultValue="United Kingdom"
                  />
                </Grid>
              </Grid>
            </Box>
            
            {editClient ? (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ mb: 1, color: theme.palette.text.primary, fontWeight: 700, fontSize: '1.5rem' }}>
                  Client portal (magic link)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Send the client a private link to view their jobs, estimates and invoices. Links expire after 90 days unless revoked.
                </Typography>
                {portalStatus?.is_active && portalStatus.portal_url ? (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Active until {portalStatus.expires_at ? new Date(portalStatus.expires_at).toLocaleDateString() : '—'}
                    {portalStatus.last_used_at
                      ? ` · last opened ${new Date(portalStatus.last_used_at).toLocaleString()}`
                      : ''}
                  </Alert>
                ) : portalStatus?.revoked_at ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>Portal link was revoked.</Alert>
                ) : (
                  <Alert severity="info" sx={{ mb: 2 }}>No active portal link yet.</Alert>
                )}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={portalBusy}
                    onClick={handleCreatePortalLink}
                    sx={{ textTransform: 'none' }}
                  >
                    {portalStatus?.is_active ? 'Regenerate link' : 'Create link'}
                  </Button>
                  {portalStatus?.portal_url ? (
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={portalBusy}
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(portalStatus.portal_url);
                          setPortalUrlCopied(true);
                          setTimeout(() => setPortalUrlCopied(false), 2500);
                        } catch {
                          setError('Could not copy link');
                        }
                      }}
                      sx={{ textTransform: 'none' }}
                    >
                      {portalUrlCopied ? 'Copied!' : 'Copy link'}
                    </Button>
                  ) : null}
                  {portalStatus?.has_token && !portalStatus?.revoked_at ? (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      disabled={portalBusy}
                      onClick={handleRevokePortalLink}
                      sx={{ textTransform: 'none' }}
                    >
                      Revoke
                    </Button>
                  ) : null}
                </Box>

                <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 700 }}>
                  Portal access log
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Office actions (create/revoke) and when the client opens the portal, views estimates or invoices.
                </Typography>
                {portalLogsLoading ? <LinearProgress sx={{ mb: 1 }} /> : null}
                {!portalLogsLoading && portalAccessLogs.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No portal activity recorded yet.
                  </Typography>
                ) : null}
                {portalAccessLogs.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 220, mb: 2 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>When</TableCell>
                          <TableCell>Action</TableCell>
                          <TableCell>By</TableCell>
                          <TableCell>IP</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {portalAccessLogs.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
                            </TableCell>
                            <TableCell>{row.action_label || row.action}</TableCell>
                            <TableCell>
                              {row.user?.name || (row.user ? row.user.email : 'Client')}
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {row.ip_address || '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : null}
              </Box>
            ) : null}

            {editClient && mayManageSubscriptions ? (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ mb: 1, color: theme.palette.text.primary, fontWeight: 700, fontSize: '1.5rem' }}>
                  Service subscriptions
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Recurring plans generate jobs automatically. Pause, resume or cancel anytime. Manage plan templates under Service plans.
                </Typography>
                {subscriptionsLoading ? <LinearProgress sx={{ mb: 2 }} /> : null}
                {subscriptions.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                    {subscriptions.map((s) => (
                      <Paper key={s.id} variant="outlined" sx={{ p: 1.5 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box>
                            <Typography sx={{ fontWeight: 700 }}>{s.service_plan?.name || `Plan #${s.service_plan_id}`}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Next: {s.next_service_date} ·{' '}
                              <Chip size="small" label={s.status} sx={{ height: 20, textTransform: 'capitalize' }} />
                              {s.last_collection ? ` · last job #${s.last_collection.id}` : ''}
                              {s.service_plan?.billing_enabled ? ' · billing on' : ''}
                            </Typography>
                            {s.billing_logs?.[0]?.invoice ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Last invoice: {s.billing_logs[0].invoice.invoice_number} ({s.billing_logs[0].invoice.total}{' '}
                                {s.billing_logs[0].invoice.paid ? 'paid' : 'unpaid'})
                              </Typography>
                            ) : null}
                            <FormControlLabel
                              sx={{ mt: 0.5 }}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={s.auto_invoice !== false}
                                  disabled={subscriptionBusy || !s.service_plan?.billing_enabled}
                                  onChange={(e) => toggleSubscriptionAutoInvoice(s, e.target.checked)}
                                />
                              }
                              label={<Typography variant="caption">Auto-invoice each cycle</Typography>}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {s.service_plan?.billing_enabled && s.status === 'active' ? (
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={subscriptionBusy}
                                onClick={() => createSubscriptionInvoice(s.id)}
                                sx={{ textTransform: 'none' }}
                              >
                                Invoice now
                              </Button>
                            ) : null}
                            {s.status === 'active' ? (
                              <Button size="small" variant="outlined" disabled={subscriptionBusy} onClick={() => subscriptionAction(s.id, 'pause')} sx={{ textTransform: 'none' }}>
                                Pause
                              </Button>
                            ) : null}
                            {s.status === 'paused' ? (
                              <Button size="small" variant="outlined" disabled={subscriptionBusy} onClick={() => subscriptionAction(s.id, 'resume')} sx={{ textTransform: 'none' }}>
                                Resume
                              </Button>
                            ) : null}
                            {s.status !== 'cancelled' ? (
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                disabled={subscriptionBusy}
                                onClick={() => {
                                  if (window.confirm('Cancel this subscription?')) subscriptionAction(s.id, 'cancel');
                                }}
                                sx={{ textTransform: 'none' }}
                              >
                                Cancel
                              </Button>
                            ) : null}
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No active subscriptions for this client.
                  </Typography>
                )}
                <Box component="form" onSubmit={handleAddSubscription} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <TextField
                    select
                    SelectProps={{ native: true }}
                    label="Plan"
                    value={subForm.service_plan_id}
                    onChange={(e) => setSubForm((f) => ({ ...f, service_plan_id: e.target.value }))}
                    required
                    fullWidth
                  >
                    <option value="">Select plan…</option>
                    {servicePlans.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.interval_count} {p.interval_unit})
                      </option>
                    ))}
                  </TextField>
                  <TextField
                    label="First service date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={subForm.next_service_date}
                    onChange={(e) => setSubForm((f) => ({ ...f, next_service_date: e.target.value }))}
                    required
                    fullWidth
                  />
                  <TextField
                    label="Preferred time"
                    value={subForm.preferred_time}
                    onChange={(e) => setSubForm((f) => ({ ...f, preferred_time: e.target.value }))}
                    fullWidth
                  />
                  <TextField
                    label="Service address override"
                    value={subForm.service_address}
                    onChange={(e) => setSubForm((f) => ({ ...f, service_address: e.target.value }))}
                    fullWidth
                  />
                  <Button type="submit" variant="contained" disabled={subscriptionBusy || !subForm.service_plan_id} sx={{ textTransform: 'none', gridColumn: { sm: '1 / -1' }, width: { xs: '100%', sm: 'auto' } }}>
                    Add subscription
                  </Button>
                </Box>
              </Box>
            ) : null}

            {/* Additional Information Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 2, color: theme.palette.text.primary, fontWeight: 700, fontSize: '1.5rem' }}>
                Additional Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="notes"
                    label="Notes"
                    value={form.notes}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    placeholder="Additional notes about the client"
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid #e2e8f0' }}>
            {editClient && (form.phone || form.email) ? (
              <Button
                variant="outlined"
                onClick={() => setSendMessageOpen(true)}
                sx={{ mr: 'auto' }}
              >
                Send message
              </Button>
            ) : null}
            <Button onClick={handleClose} sx={{ color: '#64748b' }}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" sx={{
              background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
              },
            }}>
              {editClient ? 'Update Client' : 'Add Client'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <SendMessageDialog
        open={sendMessageOpen}
        onClose={() => setSendMessageOpen(false)}
        defaultChannel={form.phone ? 'sms' : 'email'}
        defaultTo={form.phone || form.email || ''}
        subjectType="client"
        subjectId={editClient?.id}
      />
    </Box>
  );
} 